import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSalesReport, getLocations, getUsers } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { localTodayKey, localMonthStartKey } from '../lib/dateLocal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
    ChartBar,
    Calendar,
    Users,
    MapPin,
    CurrencyDollar,
    TrendUp,
    FileArrowDown,
    Funnel
} from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ReportsPage = () => {
    const { isAdmin, can } = useAuth();
    const navigate = useNavigate();
    const canExport = isAdmin() || can('reports.export');
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [locations, setLocations] = useState([]);
    const [sellers, setSellers] = useState([]);
    const [products, setProducts] = useState([]);
    const [report, setReport] = useState(null);
    const [searchParams] = useSearchParams();
    const isTodayRange = searchParams.get('range') === 'today';
    const [filters, setFilters] = useState({
        start_date: isTodayRange ? localTodayKey() : localMonthStartKey(),
        end_date: localTodayKey(),
        location_id: '',
        seller_id: '',
        operation_type: 'all',
        product_ids: [],
        payment_method: 'all',
        tpv_id: 'all',
    });
    const [productPickerOpen, setProductPickerOpen] = useState(false);
    const [tpvs, setTpvs] = useState([]);

    const handleExport = async () => {
        if (!report || !report.sales_detail?.length) {
            toast.error('Genera primero el reporte para poder exportarlo');
            return;
        }
        setExporting(true);
        try {
            const resp = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/reports/sales/export`,
                filters,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    responseType: 'blob'
                }
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `reporte_ventas_${filters.start_date}_${filters.end_date}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success('Reporte descargado');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al exportar');
        } finally {
            setExporting(false);
        }
    };

    const formatMoney = (v) => `$${(Number(v) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const paymentLabel = (m) => ({
        cash: '💵 Efectivo', credit_card: '💳 Crédito', debit_card: '💳 Débito',
        transfer: '🏦 Transferencia', courtesy: '🎁 Cortesía',
    }[m] || m);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [locationsRes, usersRes, productsRes, tpvsRes] = await Promise.all([
                getLocations(),
                isAdmin() ? getUsers() : Promise.resolve({ data: [] }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products?active_only=true`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }).catch(() => ({ data: [] })),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payment-terminals`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }).catch(() => ({ data: [] }))
            ]);
            setLocations(locationsRes.data);
            setSellers(usersRes.data.filter(u => u.role === 'seller'));
            setProducts(productsRes.data || []);
            setTpvs((tpvsRes.data || []).filter(t => t.is_active !== false));
            // Fetch initial report
            await fetchReport();
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const response = await getSalesReport(filters);
            setReport(response.data);
        } catch (error) {
            toast.error('Error al generar reporte');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit',
            month: 'short'
        });
    };

    // Prepare chart data
    const chartData = report?.by_seller?.map(s => ({
        name: s.seller_name?.split(' ')[0] || 'Unknown',
        ventas: s.count,
        monto: s.amount
    })) || [];

    return (
        <div className="space-y-6" data-testid="reports-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Reportes de Ventas
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        Análisis y estadísticas de certificados vendidos
                    </p>
                </div>
                {canExport && report && (
                    <Button
                        onClick={handleExport}
                        disabled={exporting || !report?.sales_detail?.length}
                        data-testid="export-report-btn"
                        className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md text-sm font-medium px-4"
                    >
                        <FileArrowDown size={16} className="mr-1.5" />
                        {exporting ? 'Exportando…' : 'Exportar a Excel'}
                    </Button>
                )}
            </div>

            {/* Filters */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardHeader className="pb-2">
                    <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                        <Funnel size={20} weight="duotone" className="text-[#FB7185]" />
                        Filtros
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Calendar size={14} />
                                Fecha Inicio
                            </Label>
                            <Input
                                type="date"
                                value={filters.start_date}
                                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                                data-testid="report-start-date"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Calendar size={14} />
                                Fecha Fin
                            </Label>
                            <Input
                                type="date"
                                value={filters.end_date}
                                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                                data-testid="report-end-date"
                                className="h-12 rounded-xl border-[#ECE3D2]"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Funnel size={14} />
                                Tipo de operación
                            </Label>
                            <Select
                                value={filters.operation_type || 'all'}
                                onValueChange={(value) => setFilters({ ...filters, operation_type: value, product_ids: value === 'sale' ? filters.product_ids : [] })}
                            >
                                <SelectTrigger data-testid="report-optype-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                    <SelectValue placeholder="Todo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todo</SelectItem>
                                    <SelectItem value="sale">Venta</SelectItem>
                                    <SelectItem value="courtesy">Cortesía</SelectItem>
                                    <SelectItem value="refund">Devolución</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {filters.operation_type === 'sale' && (
                            <div className="space-y-2 relative">
                                <Label className="flex items-center gap-2">
                                    <ChartBar size={14} />
                                    Productos
                                </Label>
                                <button
                                    type="button"
                                    onClick={() => setProductPickerOpen(!productPickerOpen)}
                                    data-testid="report-products-toggle"
                                    className="h-12 w-full rounded-xl border border-[#ECE3D2] bg-white px-3 text-left text-sm flex items-center justify-between hover:border-[#1F2D4B]/30"
                                >
                                    <span className="truncate">
                                        {filters.product_ids?.length
                                            ? `${filters.product_ids.length} producto(s)`
                                            : 'Todas'}
                                    </span>
                                    <Funnel size={14} className="text-[#8B8474] flex-shrink-0" />
                                </button>
                                {productPickerOpen && (
                                    <div
                                        data-testid="report-products-list"
                                        className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-[#ECE3D2] bg-white shadow-lg p-2 space-y-1"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setFilters({ ...filters, product_ids: [] })}
                                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-[#F8F4EC] rounded text-[#4B5878]"
                                        >
                                            Todas (limpiar selección)
                                        </button>
                                        {products.map((p) => {
                                            const selected = filters.product_ids?.includes(p.id);
                                            return (
                                                <label
                                                    key={p.id}
                                                    className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded cursor-pointer ${selected ? 'bg-[#FB7185]/10' : 'hover:bg-[#F8F4EC]'}`}
                                                    data-testid={`report-product-option-${p.id}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selected || false}
                                                        onChange={(e) => {
                                                            const next = new Set(filters.product_ids || []);
                                                            if (e.target.checked) next.add(p.id);
                                                            else next.delete(p.id);
                                                            setFilters({ ...filters, product_ids: Array.from(next) });
                                                        }}
                                                    />
                                                    <span className="font-mono text-[#8B8474]">{p.product_code || ''}</span>
                                                    <span className="text-[#1F2D4B] truncate flex-1">{p.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                        
                        {isAdmin() && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <MapPin size={14} />
                                    Ubicación
                                </Label>
                                <Select
                                    value={filters.location_id}
                                    onValueChange={(value) => setFilters({ ...filters, location_id: value === 'all' ? '' : value })}
                                >
                                    <SelectTrigger data-testid="report-location-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                        <SelectValue placeholder="Todas" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {locations.map((location) => (
                                            <SelectItem key={location.id} value={location.id}>
                                                {location.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        {isAdmin() && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Users size={14} />
                                    Vendedor
                                </Label>
                                <Select
                                    value={filters.seller_id}
                                    onValueChange={(value) => setFilters({ ...filters, seller_id: value === 'all' ? '' : value })}
                                >
                                    <SelectTrigger data-testid="report-seller-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                        <SelectValue placeholder="Todos" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todos</SelectItem>
                                        {sellers.map((seller) => (
                                            <SelectItem key={seller.id} value={seller.id}>
                                                {seller.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                Forma de pago
                            </Label>
                            <Select
                                value={filters.payment_method}
                                onValueChange={(value) => setFilters({
                                    ...filters,
                                    payment_method: value,
                                    // Reset TPV cuando cambia el método (o cuando es efectivo)
                                    tpv_id: (value === 'credit_card' || value === 'debit_card') ? (filters.tpv_id || 'all') : 'all',
                                })}
                            >
                                <SelectTrigger data-testid="report-payment-method-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    <SelectItem value="cash">💵 Efectivo</SelectItem>
                                    <SelectItem value="credit_card">💳 Tarjeta de crédito</SelectItem>
                                    <SelectItem value="debit_card">💳 Tarjeta de débito</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {(filters.payment_method === 'credit_card' || filters.payment_method === 'debit_card') && (
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    Terminal (TPV)
                                </Label>
                                <Select
                                    value={filters.tpv_id || 'all'}
                                    onValueChange={(value) => setFilters({ ...filters, tpv_id: value })}
                                >
                                    <SelectTrigger data-testid="report-tpv-select" className="h-12 rounded-xl border-[#ECE3D2]">
                                        <SelectValue placeholder="Todas las TPVs" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas las TPVs</SelectItem>
                                        {tpvs.map((t) => (
                                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        <div className="flex items-end">
                            <Button
                                onClick={fetchReport}
                                disabled={loading}
                                data-testid="generate-report-btn"
                                className="w-full h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                    <>
                                        <ChartBar size={18} className="mr-2" />
                                        Generar
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {report && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5878] mb-1">
                                            Total Ventas
                                        </p>
                                        <p className="font-editorial text-3xl font-semibold text-[#1F2D4B]" data-testid="kpi-total-sales">
                                            {report.total_sales}
                                        </p>
                                        <p className="text-sm text-[#4B5878] mt-1">{report.total_certificates || 0} certificados</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-[#FB7185] flex items-center justify-center">
                                        <TrendUp size={24} weight="duotone" className="text-white" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5878] mb-1">
                                            Ingresos Totales
                                        </p>
                                        <p className="font-editorial text-3xl font-semibold text-[#059669]" data-testid="kpi-total-amount">
                                            {formatCurrency(report.total_amount)}
                                        </p>
                                        <p className="text-sm text-[#4B5878] mt-1">en el período</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-[#059669] flex items-center justify-center">
                                        <CurrencyDollar size={24} weight="duotone" className="text-white" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5878] mb-1">
                                            Promedio por Venta
                                        </p>
                                        <p className="font-editorial text-3xl font-semibold text-[#1F2D4B]">
                                            {formatCurrency(report.total_sales > 0 ? report.total_amount / report.total_sales : 0)}
                                        </p>
                                        <p className="text-sm text-[#4B5878] mt-1">ticket promedio</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-[#F8F4EC] border border-[#ECE3D2] flex items-center justify-center">
                                        <ChartBar size={24} weight="duotone" className="text-[#1F2D4B]" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-white border-[#ECE3D2] rounded-2xl" data-testid="kpi-courtesies-card">
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5878] mb-1">
                                            Cortesías
                                        </p>
                                        <p className="font-editorial text-3xl font-semibold text-[#7C3AED]" data-testid="kpi-courtesies-count">
                                            {report.courtesies_count || 0}
                                        </p>
                                        <p className="text-sm text-[#4B5878] mt-1">{report.courtesies_certificates || 0} certificados otorgados</p>
                                    </div>
                                    <div className="w-12 h-12 rounded-xl bg-[#7C3AED] flex items-center justify-center">
                                        <span className="text-white text-xl">🎁</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Chart */}
                    {chartData.length > 0 && (
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                            <CardHeader>
                                <CardTitle className="font-editorial text-lg text-[#1F2D4B]">
                                    Ventas por Vendedor
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                            <XAxis dataKey="name" tick={{ fill: '#64748B', fontSize: 12 }} />
                                            <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
                                            <Tooltip 
                                                contentStyle={{ 
                                                    backgroundColor: '#fff', 
                                                    border: '1px solid #E5E7EB',
                                                    borderRadius: '12px'
                                                }}
                                                formatter={(value, name) => [
                                                    name === 'monto' ? formatCurrency(value) : value,
                                                    name === 'monto' ? 'Monto' : 'Ventas'
                                                ]}
                                            />
                                            <Bar dataKey="ventas" fill="#FB7185" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Seller Performance Table */}
                    {report.by_seller?.length > 0 && (
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                            <CardHeader className="border-b border-[#ECE3D2] bg-[#F8F4EC]">
                                <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                                    <Users size={20} weight="duotone" className="text-[#FB7185]" />
                                    Desempeño por Vendedor
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent">
                                            <TableHead className="font-semibold text-[#1F2D4B]">#</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Vendedor</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Ventas</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Monto Total</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Promedio</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {report.by_seller.map((seller, index) => (
                                            <TableRow key={seller.seller_id} className="hover:bg-[#F8F4EC]/50">
                                                <TableCell className="font-medium text-[#4B5878]">
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell className="font-medium text-[#1F2D4B]">
                                                    {seller.seller_name}
                                                </TableCell>
                                                <TableCell>{seller.count}</TableCell>
                                                <TableCell className="font-semibold text-[#059669]">
                                                    {formatCurrency(seller.amount)}
                                                </TableCell>
                                                <TableCell className="text-[#4B5878]">
                                                    {formatCurrency(seller.count > 0 ? seller.amount / seller.count : 0)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Recent Sales */}
                    {report.sales?.length > 0 && (
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                            <CardHeader className="border-b border-[#ECE3D2] bg-[#F8F4EC]">
                                <CardTitle className="font-editorial text-lg text-[#1F2D4B]">
                                    Últimas Ventas del Período
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent">
                                            <TableHead className="font-semibold text-[#1F2D4B]">Folio</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Fecha</TableHead>
                                            <TableHead className="font-semibold text-[#1F2D4B]">Monto</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {report.sales.slice(0, 10).map((sale) => (
                                            <TableRow key={sale.id} className="hover:bg-[#F8F4EC]/50">
                                                <TableCell className="font-mono font-medium text-[#FB7185]">
                                                    {sale.folio}
                                                </TableCell>
                                                <TableCell className="text-[#4B5878]">
                                                    {formatDate(sale.created_at)}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {formatCurrency(sale.amount)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    )}

                    {/* Detalle de ventas — fila por venta con toda la info solicitada */}
                    {report.sales_detail && report.sales_detail.length > 0 && (
                        <Card className="bg-white border-[#ECE3D2] rounded-2xl" data-testid="sales-detail-card">
                            <CardHeader className="pb-2 flex flex-row items-center justify-between">
                                <CardTitle className="font-editorial text-lg text-[#1F2D4B] flex items-center gap-2">
                                    <ChartBar size={20} weight="duotone" className="text-[#1F2D4B]" />
                                    Detalle de ventas ({report.sales_detail.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="text-xs">Tipo</TableHead>
                                                <TableHead className="text-xs">Fecha</TableHead>
                                                <TableHead className="text-xs">Hora</TableHead>
                                                <TableHead className="text-xs">Folio(s)</TableHead>
                                                <TableHead className="text-xs">Cliente</TableHead>
                                                <TableHead className="text-xs">Teléfono</TableHead>
                                                <TableHead className="text-xs">Promotora</TableHead>
                                                <TableHead className="text-xs">Sucursal</TableHead>
                                                <TableHead className="text-xs text-right">Monto</TableHead>
                                                <TableHead className="text-xs">Forma de pago</TableHead>
                                                <TableHead className="text-xs">Tarjeta</TableHead>
                                                <TableHead className="text-xs">TPV</TableHead>
                                                <TableHead className="text-xs">Nº Operación</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {report.sales_detail.map((s) => (
                                                <TableRow key={s.refund_id || s.sale_id} data-testid={`sales-row-${s.refund_id || s.sale_id}`}>
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        {s.kind === 'refund' ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full bg-rose-100 text-rose-700 font-semibold uppercase tracking-wide">Devolución</span>
                                                        ) : s.status === 'cancelled' ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full bg-gray-200 text-gray-700 font-semibold uppercase tracking-wide line-through" data-testid={`sale-cancelled-badge-${s.sale_id}`}>Cancelado</span>
                                                        ) : s.kind === 'courtesy' || s.is_courtesy ? (
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full bg-violet-100 text-violet-700 font-semibold uppercase tracking-wide">Cortesía</span>
                                                        ) : (
                                                            <span className="inline-flex items-center px-2 py-0.5 text-[10px] rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">Venta</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap">{s.date}</TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap text-[#8B8474]">{s.time}</TableCell>
                                                    <TableCell className="text-xs font-mono">
                                                        {s.folios && s.folios.length > 0 ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {s.folios.map((f, i) => (
                                                                    <span key={`${s.sale_id}-${i}`}>{f}</span>
                                                                ))}
                                                            </div>
                                                        ) : (s.folio || '—')}
                                                    </TableCell>
                                                    <TableCell className="text-xs">
                                                        {s.customer_id ? (
                                                            <button
                                                                onClick={() => navigate(`/customer-directory?customer=${s.customer_id}`)}
                                                                data-testid={`customer-link-${s.customer_id}`}
                                                                className="text-[#1F2D4B] hover:text-[#FB7185] hover:underline font-medium"
                                                            >
                                                                {s.customer_name}
                                                            </button>
                                                        ) : (
                                                            <span className="text-[#8B8474]">{s.customer_name}</span>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap text-[#8B8474]" data-testid={`sales-row-phone-${s.sale_id}`}>
                                                        {s.customer_phone || '—'}
                                                    </TableCell>
                                                    <TableCell className="text-xs">{s.seller_name}</TableCell>
                                                    <TableCell className="text-xs">{s.location_name}</TableCell>
                                                    <TableCell className="text-xs text-right font-semibold whitespace-nowrap">
                                                        {formatMoney(s.total_amount)}
                                                    </TableCell>
                                                    <TableCell className="text-xs whitespace-nowrap">
                                                        {s.is_mixed ? (
                                                            <div className="flex flex-col gap-0.5" data-testid={`sales-row-mixed-${s.sale_id}`}>
                                                                <span className="text-[10px] bg-[#FB7185]/10 text-[#FB7185] px-1.5 py-0.5 rounded font-semibold uppercase w-fit">
                                                                    Mixto
                                                                </span>
                                                                {(s.payments_breakdown || []).map((p, idx) => (
                                                                    <span key={p.id || idx} className="text-[10px] text-[#4B5878]">
                                                                        {paymentLabel(p.method)} · {formatMoney(p.amount)}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            paymentLabel(s.payment_methods?.[0] || s.payment_method)
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="text-xs font-mono text-[#8B8474]">
                                                        {s.is_mixed && (s.payments_breakdown || []).some(p => p.card_last4) ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {(s.payments_breakdown || []).map((p, idx) => (
                                                                    <span key={p.id || idx}>{p.card_last4 ? `••${p.card_last4}` : '—'}</span>
                                                                ))}
                                                            </div>
                                                        ) : (s.card_last4 ? `••${s.card_last4}` : '—')}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-[#8B8474]">
                                                        {s.is_mixed && (s.payments_breakdown || []).some(p => p.tpv_name || p.tpv_id) ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {(s.payments_breakdown || []).map((p, idx) => (
                                                                    <span key={p.id || idx}>{p.tpv_name || p.tpv_id || '—'}</span>
                                                                ))}
                                                            </div>
                                                        ) : (s.tpv_name || s.tpv_id || '—')}
                                                    </TableCell>
                                                    <TableCell className="text-xs font-mono text-[#8B8474]">
                                                        {s.is_mixed && (s.payments_breakdown || []).some(p => p.operation_number) ? (
                                                            <div className="flex flex-col gap-0.5">
                                                                {(s.payments_breakdown || []).map((p, idx) => (
                                                                    <span key={p.id || idx}>{p.operation_number || '—'}</span>
                                                                ))}
                                                            </div>
                                                        ) : (s.operation_number || '—')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default ReportsPage;
